import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../../api/axios';

const ClientList = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [pagination, setPagination] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [deactivateModal, setDeactivateModal] = useState(null);
  const [deactivateReason, setDeactivateReason] = useState('');
  const [deactivating, setDeactivating] = useState(false);

  const fetchClients = async (page = 1) => {
    setLoading(true);
    try {
      const params = { page };
      if (search) params.search = search;
      if (status) params.status = status;
      if (includeInactive) params.include_inactive = 1;
      const response = await api.get('/clients', { params });
      setClients(response.data.data);
      setPagination(response.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
    fetchClients(1);
  }, [search, status, includeInactive]);

  const handleDeactivate = async () => {
    if (!deactivateModal) return;
    setDeactivating(true);
    try {
      const res = await api.delete(`/clients/${deactivateModal.id}`, {
        data: { deactivation_reason: deactivateReason }
      });
      toast.success(`Cliente desactivado. ${res.data.affected_case_files > 0 ? `${res.data.affected_case_files} expediente(s) activo(s) quedan en el historial.` : ''}`);
      setDeactivateModal(null);
      setDeactivateReason('');
      fetchClients(currentPage);
    } catch (error) {
      toast.error('Error al desactivar cliente');
    } finally {
      setDeactivating(false);
    }
  };

  const handleReactivate = async (client) => {
    try {
      await api.post(`/clients/${client.id}/reactivate`);
      toast.success('Cliente reactivado');
      fetchClients(currentPage);
    } catch (error) {
      toast.error('Error al reactivar');
    }
  };

  const getStatusBadge = (status) => {
    const badges = { active: 'badge-success', inactive: 'badge-warning', completed: 'badge-secondary' };
    const labels = { active: 'Activo', inactive: 'Inactivo', completed: 'Completado' };
    return <span className={`badge ${badges[status]}`}>{labels[status]}</span>;
  };

  return (
    <div className="container-fluid">
      {/* Header */}
      <div className="page-header d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0">
          Clientes
          {pagination.total > 0 && (
            <span className="badge badge-secondary ml-2">{pagination.total}</span>
          )}
        </h4>
        <Link to="/clients/create" className="btn btn-primary">
          <i className="ti-plus mr-1"></i> Nuevo Cliente
        </Link>
      </div>

      {/* Filtros */}
      <div className="card mb-3">
        <div className="card-body py-3">
          <div className="row filter-row align-items-center">
            <div className="col-12 col-md-6 mb-2 mb-md-0">
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Buscar por nombre, email, teléfono o contacto..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ paddingLeft: '38px', fontSize: '14px' }}
                  autoComplete="off"
                />
                <svg style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', width: 16, height: 16, color: '#9CA3AF', pointerEvents: 'none' }}
                  fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                {search && (
                  <button onClick={() => setSearch('')} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF', padding: 0 }}>
                    <svg style={{ width: 16, height: 16 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
            <div className="col-6 col-md-3 mb-2 mb-md-0">
              <select className="form-control" value={status}
                onChange={(e) => setStatus(e.target.value)}
                style={{ fontSize: '14px' }}>
                <option value="">Todos los estados</option>
                <option value="active">Activo</option>
                <option value="inactive">Inactivo</option>
                <option value="completed">Completado</option>
              </select>
            </div>
            <div className="col-6 col-md-3">
              <div className="custom-control custom-checkbox">
                <input type="checkbox" className="custom-control-input" id="includeInactive"
                  checked={includeInactive}
                  onChange={(e) => setIncludeInactive(e.target.checked)} />
                <label className="custom-control-label" htmlFor="includeInactive">
                  Ver desactivados
                </label>
              </div>
            </div>
          </div>
          {search && (
            <div className="mt-2">
              <small className="text-muted">
                Buscando: <strong>"{search}"</strong>
                {pagination.total > 0 ? ` — ${pagination.total} resultado(s)` : ' — Sin resultados'}
              </small>
            </div>
          )}
        </div>
      </div>

      {/* Tabla / Cards */}
      <div className="card">
        <div className="card-body p-0 mobile-card-list">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary"></div>
            </div>
          ) : clients.length === 0 ? (
            <div className="text-center text-muted py-5">No hay clientes registrados</div>
          ) : (
            <>
              <table className="table table-hover mb-0">
                <thead>
                  <tr>
                    <th>Nombre</th>
                    <th className="mobile-hide">Contacto</th>
                    <th className="mobile-hide">Teléfono</th>
                    <th className="mobile-hide">Email</th>
                    <th className="mobile-hide">Responsable</th>
                    <th>Estado</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {clients.map((client) => (
                    <tr key={client.id}
                      style={{
                        opacity: !client.active ? 0.6 : 1,
                        backgroundColor: !client.active ? '#f8f9fa' : 'inherit'
                      }}>
                      {/* Nombre — siempre visible */}
                      <td className="td-name">
                        <Link to={`/clients/${client.id}`} className="font-weight-bold">
                          {client.name}
                        </Link>
                        {!client.active && (
                          <span className="badge badge-danger ml-2">Desactivado</span>
                        )}
                      </td>

                      {/* Contacto — oculto en móvil */}
                      <td className="mobile-hide" data-label="Contacto">
                        {client.contact_name ? (
                          <div>
                            <small className="font-weight-bold">{client.contact_name}</small>
                            {client.contact_role && (
                              <small className="text-muted ml-1">({client.contact_role})</small>
                            )}
                          </div>
                        ) : '-'}
                      </td>

                      {/* Teléfono — visible en móvil como label */}
                      <td data-label="Tel" className="mobile-hide">{client.phone || '-'}</td>

                      {/* Email — oculto en móvil */}
                      <td className="mobile-hide" data-label="Email">{client.email || '-'}</td>

                      {/* Responsable — oculto en móvil */}
                      <td className="mobile-hide" data-label="Responsable">
                        {client.assigned_user?.name || '-'}
                      </td>

                      {/* Estado — siempre visible */}
                      <td data-label="Estado">{getStatusBadge(client.status)}</td>

                      {/* Acciones — siempre visible */}
                      <td className="td-actions">
                        <div className="d-flex" style={{ gap: '6px' }}>
                          <Link to={`/clients/${client.id}`} className="btn btn-sm btn-outline-primary">
                            <i className="ti-eye"></i>
                          </Link>
                          {client.active ? (
                            <>
                              <Link to={`/clients/${client.id}/edit`} className="btn btn-sm btn-outline-secondary">
                                <i className="ti-pencil"></i>
                              </Link>
                              <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => { setDeactivateModal(client); setDeactivateReason(''); }}
                                title="Desactivar cliente"
                              >
                                <i className="ti-close"></i>
                              </button>
                            </>
                          ) : (
                            <button
                              className="btn btn-sm btn-outline-success"
                              onClick={() => handleReactivate(client)}
                              title="Reactivar cliente"
                            >
                              <i className="ti-reload"></i>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Paginación */}
              {pagination.last_page > 1 && (
                <div className="d-flex justify-content-between align-items-center px-3 py-3 border-top flex-wrap" style={{ gap: '8px' }}>
                  <small className="text-muted">
                    Mostrando {pagination.from}–{pagination.to} de {pagination.total} clientes
                  </small>
                  <ul className="pagination pagination-sm mb-0">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => { setCurrentPage(p => p - 1); fetchClients(currentPage - 1); }}>‹</button>
                    </li>
                    {Array.from({ length: pagination.last_page }, (_, i) => i + 1).map(page => (
                      <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                        <button className="page-link" onClick={() => { setCurrentPage(page); fetchClients(page); }}>{page}</button>
                      </li>
                    ))}
                    <li className={`page-item ${currentPage === pagination.last_page ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => { setCurrentPage(p => p + 1); fetchClients(currentPage + 1); }}>›</button>
                    </li>
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Modal de desactivación */}
      {deactivateModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '16px',
        }}>
          <div style={{
            backgroundColor: '#fff', borderRadius: '12px', padding: '28px',
            maxWidth: '500px', width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <div style={{ width: 60, height: 60, borderRadius: '50%', backgroundColor: '#FEE2E2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                <svg style={{ width: 28, height: 28, color: '#EF4444' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h5 style={{ marginBottom: '8px' }}>Desactivar Cliente</h5>
              <p style={{ color: '#6B7280', fontSize: '14px', marginBottom: 0 }}>
                Estás a punto de desactivar a <strong>{deactivateModal.name}</strong>.
              </p>
            </div>

            <div style={{ backgroundColor: '#FEF3C7', borderRadius: '8px', padding: '12px 16px', marginBottom: '20px', fontSize: '13px', color: '#92400E' }}>
              <strong>⚠️ Advertencia:</strong> El cliente y toda su información se conservará como historial, pero ya no aparecerá en las listas activas.
            </div>

            <div className="form-group">
              <label style={{ fontSize: '13px', fontWeight: '600' }}>Motivo de desactivación (opcional)</label>
              <textarea
                className="form-control"
                rows="3"
                placeholder="Ej. Trámite finalizado, cliente solicitó cancelar, etc."
                value={deactivateReason}
                onChange={(e) => setDeactivateReason(e.target.value)}
              ></textarea>
            </div>

            <div className="d-flex justify-content-end" style={{ gap: '12px' }}>
              <button className="btn btn-outline-secondary"
                onClick={() => { setDeactivateModal(null); setDeactivateReason(''); }}
                disabled={deactivating}>
                Cancelar
              </button>
              <button className="btn btn-danger" onClick={handleDeactivate} disabled={deactivating}>
                {deactivating ? 'Desactivando...' : 'Sí, desactivar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientList;